import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import ComicCard from "@/components/feature/ComicCard";
import NewsCard from "@/components/feature/NewsCard";
import EmptyState from "@/components/feature/EmptyState";
import { getPopularComics, getSortedNews, searchComics, searchNews } from "@/utils/comic";

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const keyword = searchParams.get("q") ?? "";
  const [input, setInput] = useState(keyword);
  const [tab, setTab] = useState<"comic" | "news">("comic");

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  const comicResults = useMemo(() => searchComics(keyword), [keyword]);
  const newsResults = useMemo(() => searchNews(keyword), [keyword]);
  const popular = getPopularComics(4);
  const latestNews = getSortedNews().slice(0, 3);

  const total = comicResults.length + newsResults.length;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  return (
    <div className="w-full">
      <section className="w-full px-4 md:px-6 pt-24 md:pt-28 pb-8 bg-background-100">
        <h1 className="text-2xl md:text-3xl font-label font-bold text-foreground-950">
          全站搜索
        </h1>
        <p className="text-sm text-foreground-600 mt-1">
          在蛙漫中搜索你感兴趣的漫画作品与动漫资讯
        </p>

        <form
          onSubmit={submit}
          className="mt-5 flex items-center gap-2 h-12 max-w-2xl px-4 rounded-full border border-background-300 bg-background-50 focus-within:border-primary-400 transition-colors"
        >
          <i className="ri-search-line text-foreground-500 text-lg"></i>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="输入漫画名称、作者或资讯关键词"
            className="flex-1 bg-transparent text-sm font-label text-foreground-900 placeholder:text-foreground-500 outline-none"
          />
          <button
            type="submit"
            className="h-9 px-5 rounded-full bg-primary-500 text-background-50 text-sm font-label font-bold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
          >
            搜索
          </button>
        </form>
      </section>

      <section className="w-full px-4 md:px-6 py-8">
        {!keyword ? (
          <div className="flex flex-col gap-10">
            <EmptyState
              icon="ri-search-line"
              title="输入关键词开始搜索"
              description="试试「星海」「武侠」「新番」这类关键词，或者从下面的热门内容开始逛。"
            />
            <div>
              <h2 className="text-lg font-label font-bold text-foreground-950 mb-4">
                热门漫画推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                {popular.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm text-foreground-600 mb-4">
              关键词「<strong className="text-primary-600">{keyword}</strong>」共找到{" "}
              <strong className="text-primary-600">{total}</strong> 条结果
            </p>

            <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 w-fit mb-6">
              <button
                type="button"
                onClick={() => setTab("comic")}
                className={`px-4 py-1.5 rounded-full text-xs md:text-sm font-label font-bold whitespace-nowrap transition-colors cursor-pointer ${
                  tab === "comic"
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:text-primary-600"
                }`}
              >
                漫画 ({comicResults.length})
              </button>
              <button
                type="button"
                onClick={() => setTab("news")}
                className={`px-4 py-1.5 rounded-full text-xs md:text-sm font-label font-bold whitespace-nowrap transition-colors cursor-pointer ${
                  tab === "news"
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-600 hover:text-primary-600"
                }`}
              >
                资讯 ({newsResults.length})
              </button>
            </div>

            {tab === "comic" ? (
              comicResults.length === 0 ? (
                <EmptyState
                  icon="ri-emotion-sad-line"
                  title="未找到相关内容"
                  description="没有匹配的漫画，换个关键词试试，或者看看下面的热门推荐。"
                  action={
                    <Link
                      to="/search"
                      className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-label font-bold"
                    >
                      重新搜索
                    </Link>
                  }
                />
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
                  {comicResults.map((comic) => (
                    <ComicCard key={comic.id} comic={comic} />
                  ))}
                </div>
              )
            ) : newsResults.length === 0 ? (
              <EmptyState
                icon="ri-emotion-sad-line"
                title="未找到相关资讯"
                description="没有匹配的资讯，换个关键词试试，或者直接浏览资讯列表。"
                action={
                  <Link
                    to="/news"
                    className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-label font-bold"
                  >
                    浏览资讯列表
                  </Link>
                }
              />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {newsResults.map((item) => (
                  <NewsCard key={item.id} news={item} variant="grid" />
                ))}
              </div>
            )}

            {total === 0 && (
              <div className="mt-12">
                <h2 className="text-lg font-label font-bold text-foreground-950 mb-4">
                  热门推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 mb-10">
                  {popular.map((comic) => (
                    <ComicCard key={comic.id} comic={comic} />
                  ))}
                </div>
                <h2 className="text-lg font-label font-bold text-foreground-950 mb-4">
                  最新资讯
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                  {latestNews.map((item) => (
                    <NewsCard key={item.id} news={item} variant="grid" />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}